import React, { useEffect, useState } from 'react';
import { Button, Dialog, FormHelperText, Grid, Icon, TextField, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Scrollbars from 'rc-scrollbars';
import DialogLoader from '../../../components/Loader/DialogLoader';
import { InputBaseField, SelectField } from '../../../components/InputField/InputField';
import { DraggableComponent, Label } from '../../../components/UiElements/UiElements';
import { withSnackbar } from '../../../components/Message/Alert';
import { PostDataAPI, PostFileDataAPI } from '../../../Services/APIService';
import useStyles from '../addInventory/styles';

const reportTypes = [
    { label: 'Full Inventory', value: 'FullInventory' },
    { label: 'Available Devices', value: 'AvailableDevices' },
    { label: 'Assigned Devices', value: 'AssignedDevices' },
    { label: 'Assignment History', value: 'AssignmentHistory' },
    { label: 'Reassignment', value: 'Reassignment' },
    { label: 'Carton Summary', value: 'CartonSummary' },
    { label: 'Activity Log', value: 'ActivityLog' },
    { label: 'Program Distribution', value: 'ProgramDistribution' },
];

const initialState = {
    reportType: 'FullInventory',
    cartonNo: '',
    fromDate: '',
    toDate: '',
};

function ExportInventory({ dialogOpenClose, handleClose, showMessage, ...props }) {
    const classes = useStyles();
    const [state, setState] = useState(initialState);
    const [cartonOptions, setCartonOptions] = useState([]);
    const [isCartonsLoading, setIsCartonsLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [dateError, setDateError] = useState('');

    useEffect(() => {
        setIsCartonsLoading(true);
        PostDataAPI('deviceinventory/getCartonList', {}).then(result => {
            setIsCartonsLoading(false);
            if (result.success) {
                setCartonOptions((result.data || []).map(item => ({
                    label: item.cartonNo,
                    value: item.cartonNo,
                })));
            } else {
                showMessage('Error', result.message || 'Error loading cartons, please contact administrator', 'error', 3000);
            }
        }).catch(() => {
            setIsCartonsLoading(false);
            showMessage('Error', 'Error loading cartons, please contact administrator', 'error', 3000);
        });
        // Cartons are refreshed each time the export dialog opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setState(previous => ({ ...previous, [name]: value }));
        if (name === 'fromDate' || name === 'toDate') {
            setDateError('');
        }
    };

    const getFileName = (contentDisposition) => {
        if (!contentDisposition) {
            return '';
        }
        const utfName = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utfName && utfName[1]) {
            return decodeURIComponent(utfName[1].replace(/["']/g, ''));
        }
        const name = contentDisposition.match(/filename="?([^";]+)"?/i);
        return name && name[1] ? name[1] : '';
    };

    const downloadFile = (response) => {
        const contentType = response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const fileName = getFileName(response.headers['content-disposition']) || `${state.reportType}.xlsx`;
        const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const showExportError = async (error) => {
        let message = 'Error exporting inventory, please contact administrator';
        const errorData = error && error.response ? error.response.data : null;
        if (errorData instanceof Blob) {
            try {
                const errorText = await errorData.text();
                const errorResult = JSON.parse(errorText);
                message = errorResult.message || errorResult.Message || message;
            } catch (parseError) {
                // Use the standard message when the server response is not JSON.
            }
        }
        showMessage('Error', message, 'error', 3000);
    };

    const exportReport = () => {
        if (isLoading) {
            return;
        }
        if (state.fromDate && state.toDate && state.fromDate > state.toDate) {
            setDateError('From Date must not be after To Date');
            return;
        }

        const request = {
            reportType: state.reportType,
            cartonNo: state.cartonNo || '',
            fromDate: state.fromDate || null,
            toDate: state.toDate || null,
        };

        setIsLoading(true);
        PostFileDataAPI('deviceinventory/exportReport', request).then(response => {
            setIsLoading(false);
            downloadFile(response);
            showMessage('Success', 'Inventory report exported successfully.', 'success', 3000);
            handleClose();
        }).catch(error => {
            setIsLoading(false);
            showExportError(error);
        });
    };

    return (
        <Dialog
            classes={{ paper: classes.dialogPaper }}
            PaperComponent={DraggableComponent}
            disableBackdropClick
            disableEscapeKeyDown
            open={dialogOpenClose}
            maxWidth="md"
            {...props}
        >
            <div className={classes.dialogContent}>
                {isLoading ? <DialogLoader /> : ''}
                <div className={classes.box}>
                    <div className={classes.header} id="draggable-dialog-title">
                        <Typography className={classes.title}>Export Inventory Report</Typography>
                        <Icon className={classes.closeIcon} onClick={isLoading ? undefined : handleClose} color="primary"><CloseIcon /></Icon>
                    </div>
                    <Scrollbars autoHeight autoHeightMax={570}>
                        <div className={classes.content}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <Label title="Report Type" size={12} mandatory={true} />
                                    <SelectField
                                        name="reportType"
                                        value={state.reportType}
                                        options={reportTypes}
                                        onChange={handleChange}
                                        IsDisabled={isLoading}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <Label title="Carton No." size={12} mandatory={false} />
                                    <Autocomplete
                                        options={cartonOptions}
                                        getOptionLabel={option => option.label || ''}
                                        getOptionSelected={(option, value) => option.value === value.value}
                                        value={cartonOptions.find(option => option.value === state.cartonNo) || null}
                                        onChange={(event, option) => setState(previous => ({
                                            ...previous,
                                            cartonNo: option ? option.value : '',
                                        }))}
                                        loading={isCartonsLoading}
                                        disabled={isLoading}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                placeholder="All Cartons / Select Carton"
                                                variant="outlined"
                                                size="small"
                                                className={classes.cartonSelect}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <Label title="From Date" size={12} mandatory={false} />
                                    <InputBaseField
                                        name="fromDate"
                                        type="date"
                                        value={state.fromDate}
                                        onChange={handleChange}
                                        IsDisabled={isLoading}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                    <Label title="To Date" size={12} mandatory={false} />
                                    <InputBaseField
                                        name="toDate"
                                        type="date"
                                        value={state.toDate}
                                        onChange={handleChange}
                                        IsDisabled={isLoading}
                                    />
                                </Grid>
                                {dateError ? (
                                    <Grid item xs={12}>
                                        <FormHelperText className={classes.errorText}>{dateError}</FormHelperText>
                                    </Grid>
                                ) : ''}
                            </Grid>
                        </div>
                    </Scrollbars>
                    <div className={classes.footer}>
                        <div className={classes.footerRight}>
                            <Button className={classes.backBtn} disabled={isLoading} onClick={handleClose}>Close</Button>
                            <Button className={classes.changeBtn} disabled={isLoading} onClick={exportReport}>Export</Button>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

export default withSnackbar(ExportInventory);
